import { FeatureIndexItem } from '../components/FeatureSearch';

// Utility to build a search index from permanent and imported layers
export function buildFeatureIndex({
  permanentGeojsons,
  importedLayers
}: {
  permanentGeojsons: Array<{ id: string; name: string; geojson: GeoJSON.FeatureCollection }>;
  importedLayers: Array<{ id: string; name: string; geojson: GeoJSON.FeatureCollection }>;
}): FeatureIndexItem[] {
  const index: FeatureIndexItem[] = [];

  // Helper to get feature name
  function getName(f: any, layerName: string) {
    // Special handling for PPEM features
    if (f.properties?.Code && f.properties?.Détenteur) {
      return `${f.properties.Code} - ${f.properties.Détenteur}`;
    }
    
    return (
      f.properties?.name ||
      f.properties?.nom ||
      f.properties?.title ||
      f.properties?.label ||
      f.properties?.Name ||
      f.properties?.Nom ||
      f.properties?.Code ||
      f.id ||
      layerName
    );
  }

  // Helper to get bounds
  function getBounds(geometry: GeoJSON.Geometry): [[number, number], [number, number]] | undefined {
    if (!geometry) return undefined;
    let coords: number[][] = [];
    if (geometry.type === 'Point') {
      const [lng, lat] = (geometry as GeoJSON.Point).coordinates as [number, number];
      return [[lat, lng], [lat, lng]];
    } else if (geometry.type === 'Polygon') {
      coords = (geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
    } else if (geometry.type === 'MultiPolygon') {
      coords = (geometry as GeoJSON.MultiPolygon).coordinates.flat(2) as [number, number][];
    } else if (geometry.type === 'LineString') {
      coords = (geometry as GeoJSON.LineString).coordinates as [number, number][];
    }
    if (coords.length === 0) return undefined;
    let minLat = coords[0][1], maxLat = coords[0][1], minLng = coords[0][0], maxLng = coords[0][0];
    coords.forEach(([lng, lat]) => {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    });
    return [[minLng, minLat], [maxLng, maxLat]];
  }

  // Index permanent layers
  permanentGeojsons.forEach(layer => {
    if (!layer.geojson || !Array.isArray(layer.geojson.features)) return;
    layer.geojson.features.forEach((f: any, i: number) => {
      index.push({
        id: f.id || `${layer.id}_${i}`,
        name: getName(f, layer.name),
        layer: layer.name,
        geometry: f.geometry,
        properties: f.properties || {},
        bounds: getBounds(f.geometry)
      });
    });
  });

  // Index imported layers
  importedLayers.forEach(layer => {
    if (!layer.geojson || !Array.isArray(layer.geojson.features)) return;
    layer.geojson.features.forEach((f: any, i: number) => {
      index.push({
        id: f.id || `${layer.id}_${i}`,
        name: getName(f, layer.name),
        layer: layer.name,
        geometry: f.geometry,
        properties: f.properties || {},
        bounds: getBounds(f.geometry)
      });
    });
  });

  return index;
}
